"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactElement, useEffect, useState } from "react";
import OpenGraphPreview, { hasOpenGraphContent, LinkPreviewCardLayout, type OpenGraphPreviewData } from "./OpenGraphPreview";
import { fetchLinkPreview } from "@/services/api/link-preview-api";
import EnsPreviewCard from "./ens/EnsPreviewCard";
import { isEnsPreview, type EnsPreview } from "./ens/types";
import { useLinkPreviewVariant, type LinkPreviewVariant } from "./LinkPreviewContext";
interface LinkPreviewCardProps {
  readonly href: string;
  readonly renderFallback: () => ReactElement;
  readonly variant?: LinkPreviewVariant | undefined;
}
type PreviewState = {
  readonly type: "loading";
  readonly href: string;
} | {
  readonly type: "fallback";
  readonly href: string;
} | {
  readonly type: "success";
  readonly href: string;
  readonly data: OpenGraphPreviewData;
} | {
  readonly type: "ens";
  readonly href: string;
  readonly data: EnsPreview;
};
const CHAT_STABLE_FRAME_CLASSES = "tw-h-[10rem] tw-min-h-[10rem] tw-max-h-[10rem] tw-w-full md:tw-h-[11rem] md:tw-min-h-[11rem] md:tw-max-h-[11rem]";
const toPreviewData = (response: Awaited<ReturnType<typeof fetchLinkPreview>>): OpenGraphPreviewData => {
  if (!response) {
    return {};
  }
  return {
    ...response,
    image: response.image ?? undefined,
    images: response.images ?? undefined,
    url: response.url ?? response.requestUrl ?? undefined,
    siteName: response.siteName ?? undefined,
    description: response.description ?? undefined,
    title: response.title ?? undefined
  };
};
export default function LinkPreviewCard(t0) {
  const $ = _c(34);
  const {
    href,
    renderFallback,
    variant
  } = t0;
  const contextVariant = useLinkPreviewVariant();
  const resolvedVariant = variant ?? contextVariant;
  const shouldUseStableFrame = resolvedVariant === "chat";
  let t1;
  if ($[0] !== href) {
    t1 = () => ({
      type: "loading",
      href
    });
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [state, setState] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== href) {
    t2 = () => {
      let active = true;
      fetchLinkPreview(href).then(response => {
        if (!active) {
          return;
        }
        if (isEnsPreview(response)) {
          setState({
            type: "ens",
            data: response,
            href
          });
          return;
        }
        const previewData = toPreviewData(response);
        if (hasOpenGraphContent(previewData)) {
          setState({
            type: "success",
            data: previewData,
            href
          });
          return;
        }
        setState({
          type: "fallback",
          href
        });
      }).catch(() => {
        if (active) {
          setState({
            type: "fallback",
            href
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t3 = [href];
    $[2] = href;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const isCurrent = state.href === href;
  let content;
  if (isCurrent && state.type === "fallback") {
    let t4;
    if ($[5] !== renderFallback) {
      t4 = renderFallback();
      $[5] = renderFallback;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const fallbackContent = t4;
    const t5 = resolvedVariant === "home" ? "tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/30 tw-p-4" : "tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4";
    let t6;
    if ($[7] !== fallbackContent) {
      t6 = <div className="tw-[overflow-wrap:anywhere] tw-flex tw-h-full tw-min-h-0 tw-w-full tw-max-w-full tw-items-center tw-justify-start tw-overflow-y-auto tw-break-words">{fallbackContent}</div>;
      $[7] = fallbackContent;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t5 || $[10] !== t6) {
      t7 = <div className={t5}>{t6}</div>;
      $[9] = t5;
      $[10] = t6;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] !== href || $[13] !== resolvedVariant || $[14] !== t7) {
      t8 = <LinkPreviewCardLayout href={href} variant={resolvedVariant}>{t7}</LinkPreviewCardLayout>;
      $[12] = href;
      $[13] = resolvedVariant;
      $[14] = t7;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    content = t8;
  } else {
    if (isCurrent && state.type === "success") {
      let t4;
      if ($[16] !== href || $[17] !== resolvedVariant || $[18] !== state.data) {
        t4 = <OpenGraphPreview href={href} preview={state.data} variant={resolvedVariant} />;
        $[16] = href;
        $[17] = resolvedVariant;
        $[18] = state.data;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      content = t4;
    } else {
      if (isCurrent && state.type === "ens") {
        const t4 = resolvedVariant === "home" ? "tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/30 tw-p-4" : "tw-h-full tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4";
        let t5;
        if ($[20] !== state.data) {
          t5 = <div className="tw-h-full tw-min-h-0 tw-overflow-y-auto"><EnsPreviewCard preview={state.data} /></div>;
          $[20] = state.data;
          $[21] = t5;
        } else {
          t5 = $[21];
        }
        let t6;
        if ($[22] !== t4 || $[23] !== t5) {
          t6 = <div className={t4}>{t5}</div>;
          $[22] = t4;
          $[23] = t5;
          $[24] = t6;
        } else {
          t6 = $[24];
        }
        let t7;
        if ($[25] !== href || $[26] !== resolvedVariant || $[27] !== t6) {
          t7 = <LinkPreviewCardLayout href={href} variant={resolvedVariant}>{t6}</LinkPreviewCardLayout>;
          $[25] = href;
          $[26] = resolvedVariant;
          $[27] = t6;
          $[28] = t7;
        } else {
          t7 = $[28];
        }
        content = t7;
      } else {
        let t4;
        if ($[29] !== href || $[30] !== resolvedVariant) {
          t4 = <OpenGraphPreview href={href} preview={undefined} variant={resolvedVariant} />;
          $[29] = href;
          $[30] = resolvedVariant;
          $[31] = t4;
        } else {
          t4 = $[31];
        }
        content = t4;
      }
    }
  }
  if (!shouldUseStableFrame) {
    return content;
  }
  let t4;
  if ($[32] !== content) {
    t4 = <div className={CHAT_STABLE_FRAME_CLASSES} data-testid="link-preview-card-stable-frame">{content}</div>;
    $[32] = content;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
}